import { useEffect, useRef, type RefObject } from 'react';

type WheelAxis = 'both' | 'horizontal';

type Options = {
  enabled?: boolean;
  /**
   * 'both'       — horizontal wheel and (boundary-gated) vertical wheel both
   *                navigate. Used by month view.
   * 'horizontal' — only horizontal-dominant gestures navigate; vertical wheel is
   *                left entirely to native scrolling (week / day time grids,
   *                year overview).
   */
  axis?: WheelAxis;
  onPrev: () => void;
  onNext: () => void;
  /**
   * The scrollable container wrapping the view. For vertical gestures in 'both'
   * mode, navigation only kicks in once this container is scrolled to the
   * matching edge — so a tall month grid can still be scrolled normally.
   */
  scrollContainer?: RefObject<HTMLElement | null>;
  /** Accumulated |delta| (px) before a step fires. */
  threshold?: number;
  /** Min time between steps so one trackpad fling = one step. */
  cooldownMs?: number;
};

/**
 * Google-Calendar-style "scroll to change period" behaviour.
 *
 * Converts deliberate wheel gestures on `ref` into prev/next navigation, with
 * delta accumulation + a cooldown so a single gesture advances exactly one
 * period instead of flinging through many.
 */
export function useWheelNavigation<T extends HTMLElement>(ref: RefObject<T>, opts: Options): void {
  const {
    enabled = true,
    axis = 'both',
    onPrev,
    onNext,
    scrollContainer,
    threshold = 48,
    cooldownMs = 500,
  } = opts;

  const accum = useRef(0);
  const cooling = useRef(false);
  // Keep latest callbacks without re-binding the listener.
  const cbRef = useRef({ onPrev, onNext });
  cbRef.current = { onPrev, onNext };

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    let resetTimer: number | undefined;
    let cooldownTimer: number | undefined;

    const handler = (e: WheelEvent) => {
      const dx = e.deltaX;
      const dy = e.deltaY;
      const horizontal = Math.abs(dx) > Math.abs(dy);

      // Vertical gestures never navigate in horizontal-only mode.
      if (axis === 'horizontal' && !horizontal) return;

      const delta = horizontal ? dx : dy;
      if (delta === 0) return;

      // Vertical gesture in 'both' mode: defer to native scrolling until the
      // container is pinned to the matching edge.
      if (!horizontal) {
        const sc = scrollContainer?.current;
        if (sc) {
          const atTop = sc.scrollTop <= 0;
          const atBottom = sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 1;
          const canScrollNatively = delta > 0 ? !atBottom : !atTop;
          if (canScrollNatively) return;
        }
      }

      // We own this gesture — stop the page from scrolling / swipe-navigating.
      e.preventDefault();

      if (cooling.current) return;

      accum.current += delta;

      // Decay the accumulator if the user pauses, so idle nudges don't add up.
      window.clearTimeout(resetTimer);
      resetTimer = window.setTimeout(() => {
        accum.current = 0;
      }, 220);

      if (Math.abs(accum.current) >= threshold) {
        const forward = accum.current > 0;
        accum.current = 0;
        cooling.current = true;
        if (forward) cbRef.current.onNext();
        else cbRef.current.onPrev();
        window.clearTimeout(cooldownTimer);
        cooldownTimer = window.setTimeout(() => {
          cooling.current = false;
        }, cooldownMs);
      }
    };

    el.addEventListener('wheel', handler, { passive: false });
    return () => {
      el.removeEventListener('wheel', handler);
      window.clearTimeout(resetTimer);
      window.clearTimeout(cooldownTimer);
    };
  }, [ref, enabled, axis, threshold, cooldownMs, scrollContainer]);
}
